import { LitElement, html } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';

import { emit } from '@amityeko/pbc-core/dist/utilities/event';
import { formatDate } from '@amityeko/pbc-core/dist/utilities/datetime';
import { withAuth } from '@amityeko/pbc-core/dist/providers/auth/consumer-mixin';

import rightArrowIcon from '@amityeko/design-system/dist/assets/icons/right-arrow';
import personIcon from '@amityeko/design-system/dist/assets/icons/person';

import '@amityeko/design-system/dist/elements/typography/tg-body-one';
import '@amityeko/design-system/dist/elements/typography/tg-body-two';
import '@amityeko/design-system/dist/elements/icon';
import '@amityeko/design-system/dist/elements/avatar';
import '@amityeko/design-system/dist/elements/dialog';
import type AmityekoDialog from '@amityeko/design-system/dist/elements/dialog';

import { RecognitionDetail } from './types';

import styles from './styles';

/**
 * @dependency amityeko-icon
 * @dependency amityeko-dialog
 * @dependency amityeko-empty-avatar
 * @dependency amityeko-tg-body-one
 * @dependency amityeko-tg-body-two
 *
 * @slot default - Element that triggers the dialog
 */
@customElement('rnr-recognition-detail')
export default class RnRRecognitionDetail extends withAuth(LitElement) {
  static styles = styles;

  @query('#dialog') dialog!: AmityekoDialog;

  @state()
  recognitionDetail?: RecognitionDetail;

  public async show(recognitionId: string) {
    this.dialog.show();

    try {
      this.recognitionDetail = await this.auth?.http?.get(`/api/v1/recognitions/${recognitionId}`);
    } catch (e: any) {
      emit(this, 'amityeko-notification--toast', {
        detail: { message: e?.error?.message, variant: 'error' },
      });
    }
  }

  private _reset() {
    this.recognitionDetail = undefined;
    emit(this, 'rnr-select-recognition-detail-close');
  }

  private _onTriggerClick() {
    this.dialog.show();
  }

  protected render() {
    return html`
      <slot @click=${this._onTriggerClick}></slot>

      <!--TODO: Add user profile from api -->
      <amityeko-dialog
        id="dialog"
        .label=${'Recognition Details'}
        @amityeko-after-hide=${this._reset}
        @amityeko-cancel=${() => this.dialog.hide()}
      >
        <div class="content">
          <div class="user-info-container">
            <div class="user-info">
              <amityeko-avatar
                shape="circle"
                label="user-avatar"
                avatarSize="56px"
                customBgColor="#f5f5f5"
              >
                <amityeko-icon slot="avatar-icon" iconSize="30px" color="#9e9e9e"
                  >${personIcon}</amityeko-icon
                >
              </amityeko-avatar>
              <amityeko-tg-body-one>
                ${this.recognitionDetail?.sender?.displayName}
              </amityeko-tg-body-one>
            </div>
            <amityeko-icon iconSize="16px" color="#757575">${rightArrowIcon}</amityeko-icon>
            <div class="user-info">
              <amityeko-avatar shape="circle" avatarSize="56px" customBgColor="#f5f5f5">
                <amityeko-icon slot="avatar-icon" iconSize="30px" color="#9e9e9e"
                  >${personIcon}</amityeko-icon
                ></amityeko-avatar
              >
              <amityeko-tg-body-one>
                ${this.recognitionDetail?.receiver?.displayName}
              </amityeko-tg-body-one>
            </div>
          </div>

          <div class="date">
            <amityeko-tg-body-two>
              ${this.recognitionDetail?.createdAt && formatDate(this.recognitionDetail?.createdAt)}
            </amityeko-tg-body-two>
          </div>
        </div>
      </amityeko-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rnr-recognition-detail': RnRRecognitionDetail;
  }
}
